import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { toast } from 'sonner';
import { Upload, ArrowLeft, CheckCircle, Camera, FileImage, Loader2, AlertTriangle, RotateCcw, LifeBuoy, ShieldAlert } from 'lucide-react';
import { downscaleImage, cloudinaryThumb } from '../../utils/imageDownscale';
import { handleSaveError, saveDraft, clearDraft, draftScope } from '../../utils/saveErrorHandler';
import TalentBackgroundCheckCard from '../../components/TalentBackgroundCheckCard';
import { consumeIntendedRoute } from '../../utils/postAuthRedirect';

const SUPPORT_EMAIL = 'support@ievntur.com';

const IDVerification = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Iter-220o — `?reupload=1` is the marker the rejection email and any other
  // recovery surface uses to force the rejected-state UI even before the API
  // call finishes loading. This avoids the "where do I upload?" dead-end.
  const reuploadMode = searchParams.get('reupload') === '1';
  const uploadSectionRef = useRef(null);
  const [idvStatus, setIdvStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({
    idFront: null,
    idBack: null,
    profilePhoto: null,
  });
  // Iter-133l — track per-field error + retry attempt so the UI can show
  // the REAL failure reason and a "Try again" button instead of a generic
  // "Failed to upload image" toast.
  const [uploadErrors, setUploadErrors] = useState({
    idFront: null,
    idBack: null,
    profilePhoto: null,
  });
  const [uploadAttempts, setUploadAttempts] = useState({
    idFront: 0,
    idBack: 0,
    profilePhoto: 0,
  });
  const [lastFiles, setLastFiles] = useState({
    idFront: null,
    idBack: null,
    profilePhoto: null,
  });
  const [documents, setDocuments] = useState({
    idFront: null,
    idBack: null,
    profilePhoto: null,
  });
  // Iter-223 — IDV expiration capture. Talent picks an ISO YYYY-MM-DD
  // OR ticks "no expiration" (rare but valid for some passport types).
  const [idExpiresAt, setIdExpiresAt] = useState('');
  const [noExpiration, setNoExpiration] = useState(false);

  // Iter-213+ P0 — Persist already-uploaded URLs so a refresh / browser
  // crash doesn't make the user re-upload images they already submitted.
  // Iter-281 P0 — keyed by the current user so admin impersonation
  // can't make Talent A's upload state appear on Talent B.
  useEffect(() => {
    if (documents.idFront || documents.idBack || documents.profilePhoto) {
      saveDraft(draftScope({ section: 'idv.documents' }), documents);
    }
  }, [documents]);

  const baseUrl = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const reportFailure = async (type, stage, errorMsg, file, attemptCount) => {
    const docType = type === 'idFront' ? 'id_front' : type === 'idBack' ? 'id_back' : 'profile_photo';
    try {
      await fetch(`${baseUrl}/api/idv/report-upload-failure`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          document_type: docType,
          stage,
          error_message: (errorMsg || '').slice(0, 500),
          file_size: file?.size || null,
          file_mime: file?.type || null,
          attempt_count: attemptCount,
        }),
      });
    } catch { /* best-effort — don't block the UI */ }
  };

  useEffect(() => {
    fetchIdvStatus();
  }, []);

  // Deep-link return: if this talent is already verified and was routed here
  // through the IDV gate while trying to reach a specific page (e.g. a tapped
  // job link), send them on to that stored destination automatically.
  useEffect(() => {
    if (idvStatus?.idv_status === 'approved') {
      const dest = consumeIntendedRoute();
      if (dest) navigate(dest, { replace: true });
    }
  }, [idvStatus, navigate]);

  // Iter-220o — When the user lands here from the rejection email
  // (?reupload=1) OR if their IDV is rejected, auto-scroll the upload
  // area into view so they never have to "find" it. Also fire a one-time
  // toast so it's crystal clear what to do next.
  useEffect(() => {
    if (loading) return;
    const isRejected = idvStatus?.idv_status === 'rejected' || reuploadMode;
    if (isRejected && uploadSectionRef.current) {
      const t = setTimeout(() => {
        uploadSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 250);
      return () => clearTimeout(t);
    }
  }, [loading, idvStatus, reuploadMode]);

  const fetchIdvStatus = async () => {
    try {
      const res = await fetch(`${baseUrl}/api/idv/status`, { headers });
      const data = await res.json();
      setIdvStatus(data);
      
      // Pre-populate existing uploads
      if (data.id_front_url) {
        setDocuments(prev => ({ ...prev, idFront: data.id_front_url }));
      }
      if (data.id_back_url) {
        setDocuments(prev => ({ ...prev, idBack: data.id_back_url }));
      }
      if (data.profile_photo_url) {
        setDocuments(prev => ({ ...prev, profilePhoto: data.profile_photo_url }));
      }
    } catch (e) {
      console.error('Error fetching IDV status:', e);
      toast.error('Failed to load verification status');
    } finally {
      setLoading(false);
    }
  };

  const uploadToCloudinary = async (file, type) => {
    setUploadProgress(prev => ({ ...prev, [type]: 'uploading' }));
    setUploadErrors(prev => ({ ...prev, [type]: null }));
    const attempt = (uploadAttempts[type] || 0) + 1;
    setUploadAttempts(prev => ({ ...prev, [type]: attempt }));

    // Iter-133l — classify the failure by STAGE so the UI can suggest the right fix.
    let stage = 'signature';
    try {
      const signatureRes = await fetch(`${baseUrl}/api/cloudinary/signature`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ folder: 'idv_documents' }),
      });
      if (!signatureRes.ok) {
        const err = `Signature request failed (HTTP ${signatureRes.status})`;
        throw new Error(err);
      }
      const { signature, timestamp, cloud_name, api_key, folder } = await signatureRes.json();

      stage = 'cloudinary_upload';
      const formData = new FormData();
      formData.append('file', file);
      formData.append('api_key', api_key);
      formData.append('timestamp', timestamp);
      formData.append('signature', signature);
      formData.append('folder', folder);

      const uploadRes = await fetch(
        `https://api.cloudinary.com/v1_1/${cloud_name}/image/upload`,
        { method: 'POST', body: formData }
      );

      if (!uploadRes.ok) {
        // Extract Cloudinary's real error reason when available.
        let detail = '';
        try {
          const body = await uploadRes.json();
          detail = body?.error?.message || '';
        } catch { /* swallow */ }
        const fmtHint = /heic|heif/i.test(file.type)
          ? ' Tip: iPhone HEIC photos sometimes fail — open the photo in the Photos app → Share → Save as File (JPEG) → re-upload.'
          : '';
        throw new Error(`Cloudinary rejected the upload (HTTP ${uploadRes.status})${detail ? ': ' + detail : ''}.${fmtHint}`);
      }

      const uploadData = await uploadRes.json();
      setDocuments(prev => ({ ...prev, [type]: uploadData.secure_url }));
      setUploadProgress(prev => ({ ...prev, [type]: 'done' }));
      setUploadErrors(prev => ({ ...prev, [type]: null }));
      setUploadAttempts(prev => ({ ...prev, [type]: 0 }));
      toast.success('Image uploaded successfully');
      return uploadData.secure_url;
    } catch (e) {
      const errMsg = e?.message || 'Upload failed for an unknown reason.';
      console.error('IDV upload error:', errMsg);
      setUploadProgress(prev => ({ ...prev, [type]: 'error' }));
      setUploadErrors(prev => ({ ...prev, [type]: errMsg }));
      reportFailure(type, stage, errMsg, file, attempt);
      toast.error(errMsg, { duration: 7000 });
      return null;
    }
  };

  // Iter-133l — reusable error banner with a 1-click retry that re-uses the
  // most recently selected file for that slot (no need to re-pick from camera roll).
  const renderUploadError = (type) => {
    const err = uploadErrors[type];
    const attempts = uploadAttempts[type] || 0;
    if (!err) return null;
    return (
      <div
        className="mt-2 border border-rose-300 bg-rose-50 rounded-md p-3 text-left"
        data-testid={`idv-error-${type}`}
      >
        <div className="flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 text-rose-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="text-sm font-semibold text-rose-900">Upload failed</div>
            <p className="text-xs text-rose-800 mt-0.5 break-words">{err}</p>
            {attempts >= 3 && (
              <p className="text-[11px] text-rose-700 mt-1">
                Having trouble? We've emailed you a few tips. You can also reply
                to that email and we'll help you upload manually.
              </p>
            )}
            <div className="mt-2 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); retryUpload(type); }}
                className="inline-flex items-center gap-1 rounded-md bg-rose-600 hover:bg-rose-700 text-white text-xs font-semibold px-2.5 py-1"
                data-testid={`idv-retry-${type}`}
              >
                <RotateCcw className="w-3 h-3" /> Try again
              </button>
              <span className="text-[11px] text-rose-600">attempt #{attempts}</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const handleFileSelect = async (e, type) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      const msg = `"${file.type || 'this file'}" isn't an image. Please upload a JPG or PNG photo of your ID.`;
      toast.error(msg, { duration: 7000 });
      setUploadErrors(prev => ({ ...prev, [type]: msg }));
      return;
    }
    
    // Validate file size (max 10MB — hard cap before we even try to downscale)
    if (file.size > 10 * 1024 * 1024) {
      const msg = `File is ${(file.size / 1024 / 1024).toFixed(1)}MB — max is 10MB. Try a different photo or compress it first.`;
      toast.error(msg, { duration: 7000 });
      setUploadErrors(prev => ({ ...prev, [type]: msg }));
      return;
    }

    // Save the original file reference so the Retry button can re-use it
    // without forcing the talent to re-select from their camera roll.
    setLastFiles((prev) => ({ ...prev, [type]: file }));

    // Downscale large camera photos client-side (up to ~85% payload reduction
    // on modern phones). Makes the upload feel instant on mobile data and
    // prevents the decode-stall that read as a "white screen" on iOS Safari.
    setUploadProgress((prev) => ({ ...prev, [type]: 'uploading' }));
    const resized = await downscaleImage(file, { maxEdge: 1600, quality: 0.85 });

    await uploadToCloudinary(resized, type);
  };

  // Iter-133l — retry the LAST file the user picked for a given slot.
  const retryUpload = async (type) => {
    const file = lastFiles[type];
    if (!file) {
      toast.info('Please re-select the image first.');
      return;
    }
    setUploadProgress((prev) => ({ ...prev, [type]: 'uploading' }));
    const resized = await downscaleImage(file, { maxEdge: 1600, quality: 0.85 });
    await uploadToCloudinary(resized, type);
  };

  const handleSubmit = async () => {
    if (!documents.idFront || !documents.idBack || !documents.profilePhoto) {
      toast.error('Please upload all required documents');
      return;
    }
    // Iter-223 — Require either an expiration date OR explicit
    // "no expiration" so the compliance scheduler has the data it needs.
    if (!noExpiration && !idExpiresAt) {
      toast.error('Please enter the ID expiration date, or check "No expiration date".');
      return;
    }
    if (!noExpiration && idExpiresAt) {
      const today = new Date(); today.setHours(0, 0, 0, 0);
      const picked = new Date(idExpiresAt + 'T00:00:00');
      if (picked <= today) {
        toast.error('ID expiration date must be in the future.');
        return;
      }
    }

    setUploading(true);

    try {
      const res = await fetch(`${baseUrl}/api/idv/upload`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_front_url: documents.idFront,
          id_back_url: documents.idBack,
          profile_photo_url: documents.profilePhoto,
          // Iter-223 — IDV expiration tracking fields
          id_expires_at: noExpiration ? null : (idExpiresAt || null),
          no_expiration: !!noExpiration,
        }),
      });
      
      const data = await res.json().catch(() => ({}));
      
      if (!res.ok) {
        // Iter-213+ P0 — synthesize an axios-shaped error so handleSaveError
        // can extract FastAPI validation arrays + log a correlation id.
        const err = new Error(data?.detail || 'Failed to submit documents');
        err.response = { status: res.status, data, headers: {} };
        err.config = { url: `${baseUrl}/api/idv/upload`, method: 'POST' };
        throw err;
      }
      
      toast.success('Documents submitted for review!');
      // Iter-213+ — clear the draft once accepted so the autosave doesn't
      // re-restore stale upload metadata on the next visit.
      clearDraft(draftScope({ section: 'idv.documents' }));
      navigate('/talent/dashboard');
    } catch (e) {
      const info = handleSaveError(e, {
        scope: draftScope({ section: 'idv.documents' }),
        endpoint: '/api/idv/upload',
        payload: {
          id_front_url: !!documents.idFront,
          id_back_url: !!documents.idBack,
          profile_photo_url: !!documents.profilePhoto,
        },
      });
      toast.error(info.message, { duration: 7000 });
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  // If already approved, show success state
  if (idvStatus?.idv_status === 'approved') {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-2xl mx-auto px-4">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/talent/dashboard')}
            className="mb-6 text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
          
          <Card className="bg-white border-gray-200 shadow-sm">
            <CardContent className="p-8 text-center">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Already Verified!</h2>
              <p className="text-gray-600">Your identity has been verified. You can be booked for gigs.</p>
            </CardContent>
          </Card>

          {/* Iter-212 — BG check surface available post-IDV too. */}
          <div className="mt-6">
            <TalentBackgroundCheckCard />
          </div>
        </div>
      </div>
    );
  }

  // If pending review
  if (idvStatus?.idv_status === 'pending_review') {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-2xl mx-auto px-4">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/talent/dashboard')}
            className="mb-6 text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
          
          <Card className="bg-white border-gray-200 shadow-sm">
            <CardContent className="p-8 text-center">
              <Loader2 className="w-16 h-16 text-amber-500 mx-auto mb-4 animate-spin" />
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Under Review</h2>
              <p className="text-gray-600 mb-4">Your documents are being reviewed by our team. You'll be notified once approved.</p>
              
              <div className="grid grid-cols-3 gap-4 mt-6">
                {idvStatus.id_front_url && (
                  <div>
                    <p className="text-xs text-gray-500 mb-2">ID Front</p>
                    <img src={cloudinaryThumb(idvStatus.id_front_url, 300)} alt="ID Front" loading="lazy" decoding="async" className="w-full h-24 object-cover rounded-lg border" />
                  </div>
                )}
                {idvStatus.id_back_url && (
                  <div>
                    <p className="text-xs text-gray-500 mb-2">ID Back</p>
                    <img src={cloudinaryThumb(idvStatus.id_back_url, 300)} alt="ID Back" loading="lazy" decoding="async" className="w-full h-24 object-cover rounded-lg border" />
                  </div>
                )}
                {idvStatus.profile_photo_url && (
                  <div>
                    <p className="text-xs text-gray-500 mb-2">Profile Photo</p>
                    <img src={cloudinaryThumb(idvStatus.profile_photo_url, 300)} alt="Profile" loading="lazy" decoding="async" className="w-full h-24 object-cover rounded-lg border" />
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Iter-220f — BG check surface is part of the talent
              compliance journey, not gated to "approved" only. Show it
              while IDV is pending review so the talent can start the
              Yardstik flow in parallel. */}
          <div className="mt-6">
            <TalentBackgroundCheckCard />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8" data-testid="idv-page">
      <div className="max-w-2xl mx-auto px-4">
        {/* Iter-313k — Verification-required banner. This is the first
            thing every unverified talent sees. Explains WHY they can't
            reach the dashboard yet, and why the "Back to Dashboard"
            button will still bounce them here until IDV is approved. */}
        <div
          className="mb-4 rounded-xl border-2 border-amber-300 bg-gradient-to-br from-amber-50 to-yellow-50 p-4 sm:p-5 shadow-sm"
          data-testid="idv-required-banner"
        >
          <div className="flex items-start gap-3">
            <div className="shrink-0 w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
              <ShieldAlert className="w-5 h-5 text-amber-600" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-base sm:text-lg font-semibold text-amber-900">
                Identity verification is required to access the platform
              </h3>
              <p className="text-sm text-amber-800 mt-1">
                For the safety of our clients, every talent and creator must complete ID verification before they can
                see gigs, apply, message, or receive payments. Uploads are reviewed within 1 business day.
              </p>
            </div>
          </div>
        </div>

        <Button
          variant="ghost"
          onClick={() => navigate('/talent/dashboard')}
          className="mb-6 text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Button>
        
        <Card className="bg-white border-gray-200 shadow-sm">
          <CardHeader>
            <CardTitle className="text-2xl text-gray-900">Identity Verification</CardTitle>
            <CardDescription className="text-gray-600">
              Upload your Driver's License (front and back) and a selfie to verify your identity.
              This is required before you can be booked for gigs or receive settlements.
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-6">
            {/* Iter-220o — Prominent rejection banner. Shows when status is
                rejected OR when the rejection email deep-linked here with
                ?reupload=1. Surfaces the rejection reason + admin notes,
                a Re-upload anchor link, and a Contact Support fallback so
                the user never feels they have to email IDs manually. */}
            {(idvStatus?.idv_status === 'rejected' || reuploadMode) && (
              <div
                className="rounded-xl border-2 border-red-300 bg-gradient-to-br from-red-50 to-amber-50 p-4 sm:p-5 shadow-sm"
                data-testid="idv-rejected-banner"
              >
                <div className="flex items-start gap-3">
                  <div className="shrink-0 w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                    <ShieldAlert className="w-5 h-5 text-red-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base sm:text-lg font-semibold text-red-900">
                      Your verification needs new uploads
                    </h3>
                    {idvStatus?.idv_reason && (
                      <p className="mt-2 text-sm text-red-800">
                        <span className="font-semibold">Reason:</span> {idvStatus.idv_reason}
                      </p>
                    )}
                    {idvStatus?.idv_admin_notes && (
                      <p className="mt-1 text-xs text-red-700/90 italic">
                        Reviewer note: {idvStatus.idv_admin_notes}
                      </p>
                    )}
                    <p className="mt-3 text-sm text-red-900 leading-relaxed">
                      Please upload the requested documents below.
                      Once submitted, our team typically reviews within 24 hours.
                    </p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <Button
                        size="sm"
                        onClick={() => uploadSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                        className="bg-red-600 hover:bg-red-700 text-white"
                        data-testid="idv-jump-to-upload"
                      >
                        <Upload className="w-4 h-4 mr-1" /> Jump to upload
                      </Button>
                      <a
                        href={`mailto:${SUPPORT_EMAIL}?subject=IDV%20Help%20-%20Re-upload%20issue&body=I%20received%20a%20rejection%20notice%20but%20need%20help%20re-uploading%20my%20ID.`}
                        data-testid="idv-contact-support"
                      >
                        <Button size="sm" variant="outline" className="border-red-300 text-red-700 hover:bg-red-100">
                          <LifeBuoy className="w-4 h-4 mr-1" /> Contact Support
                        </Button>
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Iter-220o — Mobile guidance banner. Shows on every entry so
                first-time uploaders also see clear instructions. */}
            <div className="sm:hidden rounded-lg border border-cyan-200 bg-cyan-50 p-3 text-xs text-cyan-900" data-testid="idv-mobile-guidance">
              Tap each tile below to upload from your camera or photo library. Make sure the entire ID is in frame, well-lit, and unobstructed.
            </div>

            <div ref={uploadSectionRef} data-testid="idv-upload-section" className="space-y-6 scroll-mt-4">

            {/* ID Front */}
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2 flex items-center gap-1.5">
                <Upload className="w-3.5 h-3.5 text-cyan-600" /> Upload Front ID
                <span className="text-[11px] font-normal text-gray-500 ml-1">(Driver's License or Passport)</span>
              </label>
              <div 
                className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors
                  ${documents.idFront 
                    ? 'border-green-300 bg-green-50' 
                    : 'border-gray-300 hover:border-cyan-400 hover:bg-cyan-50'
                  }`}
                onClick={() => document.getElementById('id-front-input')?.click()}
              >
                {documents.idFront ? (
                  <div className="flex items-center justify-center gap-4">
                    <img 
                      src={cloudinaryThumb(documents.idFront, 300)} 
                      alt="ID Front" 
                      loading="lazy"
                      decoding="async"
                      className="h-24 object-contain rounded"
                    />
                    <div className="text-left">
                      <CheckCircle className="w-6 h-6 text-green-500 mb-1" />
                      <p className="text-sm text-green-700">Uploaded</p>
                      <p className="text-xs text-gray-500">Click to replace</p>
                    </div>
                  </div>
                ) : uploadProgress.idFront === 'uploading' ? (
                  <div className="flex flex-col items-center">
                    <Loader2 className="w-8 h-8 text-cyan-500 animate-spin mb-2" />
                    <p className="text-sm text-gray-600">Uploading...</p>
                  </div>
                ) : (
                  <div className="flex flex-col items-center">
                    <FileImage className="w-8 h-8 text-gray-400 mb-2" />
                    <p className="text-sm text-gray-600">Click to upload front of ID</p>
                    <p className="text-xs text-gray-400 mt-1">JPG, PNG up to 10MB</p>
                  </div>
                )}
                <input
                  id="id-front-input"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => handleFileSelect(e, 'idFront')}
                  data-testid="id-front-input"
                />
              </div>
              {renderUploadError('idFront')}
            </div>
            
            {/* ID Back */}
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2 flex items-center gap-1.5">
                <Upload className="w-3.5 h-3.5 text-cyan-600" /> Upload Back ID
                <span className="text-[11px] font-normal text-gray-500 ml-1">(Back of ID — skip if passport)</span>
              </label>
              <div 
                className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors
                  ${documents.idBack 
                    ? 'border-green-300 bg-green-50' 
                    : 'border-gray-300 hover:border-cyan-400 hover:bg-cyan-50'
                  }`}
                onClick={() => document.getElementById('id-back-input')?.click()}
              >
                {documents.idBack ? (
                  <div className="flex items-center justify-center gap-4">
                    <img 
                      src={cloudinaryThumb(documents.idBack, 300)} 
                      alt="ID Back" 
                      loading="lazy"
                      decoding="async"
                      className="h-24 object-contain rounded"
                    />
                    <div className="text-left">
                      <CheckCircle className="w-6 h-6 text-green-500 mb-1" />
                      <p className="text-sm text-green-700">Uploaded</p>
                      <p className="text-xs text-gray-500">Click to replace</p>
                    </div>
                  </div>
                ) : uploadProgress.idBack === 'uploading' ? (
                  <div className="flex flex-col items-center">
                    <Loader2 className="w-8 h-8 text-cyan-500 animate-spin mb-2" />
                    <p className="text-sm text-gray-600">Uploading...</p>
                  </div>
                ) : (
                  <div className="flex flex-col items-center">
                    <FileImage className="w-8 h-8 text-gray-400 mb-2" />
                    <p className="text-sm text-gray-600">Click to upload back of ID</p>
                    <p className="text-xs text-gray-400 mt-1">JPG, PNG up to 10MB</p>
                  </div>
                )}
                <input
                  id="id-back-input"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => handleFileSelect(e, 'idBack')}
                  data-testid="id-back-input"
                />
              </div>
              {renderUploadError('idBack')}
            </div>
            
            {/* Selfie / Profile Photo */}
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2 flex items-center gap-1.5">
                <Camera className="w-3.5 h-3.5 text-cyan-600" /> Upload Selfie
                <span className="text-[11px] font-normal text-gray-500 ml-1">(Clear front-facing headshot)</span>
              </label>
              <div 
                className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors
                  ${documents.profilePhoto 
                    ? 'border-green-300 bg-green-50' 
                    : 'border-gray-300 hover:border-cyan-400 hover:bg-cyan-50'
                  }`}
                onClick={() => document.getElementById('profile-photo-input')?.click()}
              >
                {documents.profilePhoto ? (
                  <div className="flex items-center justify-center gap-4">
                    <img 
                      src={cloudinaryThumb(documents.profilePhoto, 300)} 
                      alt="Profile" 
                      loading="lazy"
                      decoding="async"
                      className="h-24 w-24 object-cover rounded-full"
                    />
                    <div className="text-left">
                      <CheckCircle className="w-6 h-6 text-green-500 mb-1" />
                      <p className="text-sm text-green-700">Uploaded</p>
                      <p className="text-xs text-gray-500">Click to replace</p>
                    </div>
                  </div>
                ) : uploadProgress.profilePhoto === 'uploading' ? (
                  <div className="flex flex-col items-center">
                    <Loader2 className="w-8 h-8 text-cyan-500 animate-spin mb-2" />
                    <p className="text-sm text-gray-600">Uploading...</p>
                  </div>
                ) : (
                  <div className="flex flex-col items-center">
                    <Camera className="w-8 h-8 text-gray-400 mb-2" />
                    <p className="text-sm text-gray-600">Click to upload or take a profile photo</p>
                    <p className="text-xs text-gray-400 mt-1">Clear, front-facing headshot</p>
                  </div>
                )}
                <input
                  id="profile-photo-input"
                  type="file"
                  accept="image/*"
                  capture="user"
                  className="hidden"
                  onChange={(e) => handleFileSelect(e, 'profilePhoto')}
                  data-testid="profile-photo-input"
                />
              </div>
              {renderUploadError('profilePhoto')}
              {/* Profile Photo Requirements */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-3" data-testid="photo-requirements">
                <p className="text-xs font-semibold text-blue-900 mb-1.5">Photo Requirements</p>
                <ul className="space-y-0.5">
                  <li className="text-xs text-blue-800 flex items-center gap-1.5"><CheckCircle className="w-3 h-3 text-blue-500 shrink-0" />Front-facing headshot</li>
                  <li className="text-xs text-blue-800 flex items-center gap-1.5"><CheckCircle className="w-3 h-3 text-blue-500 shrink-0" />No sunglasses</li>
                  <li className="text-xs text-blue-800 flex items-center gap-1.5"><CheckCircle className="w-3 h-3 text-blue-500 shrink-0" />No heavy filters or beauty filters</li>
                  <li className="text-xs text-blue-800 flex items-center gap-1.5"><CheckCircle className="w-3 h-3 text-blue-500 shrink-0" />Clear lighting and visible face</li>
                  <li className="text-xs text-blue-800 flex items-center gap-1.5"><CheckCircle className="w-3 h-3 text-blue-500 shrink-0" />Professional appearance</li>
                </ul>
              </div>
            </div>

            {/* Iter-223 — IDV expiration capture. Required so the
                compliance scheduler can fire 60/30/0-day reminders. */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-2" data-testid="idv-expiration-section">
              <p className="text-sm font-semibold text-amber-900">ID Expiration Date</p>
              <p className="text-xs text-amber-800">
                Enter the expiration date printed on your ID. We&apos;ll remind you to renew
                60 / 30 days before it expires.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 mt-2">
                <input
                  type="date"
                  value={idExpiresAt}
                  onChange={(e) => { setIdExpiresAt(e.target.value); if (e.target.value) setNoExpiration(false); }}
                  disabled={noExpiration}
                  min={new Date().toISOString().slice(0, 10)}
                  className="flex-1 px-3 py-2 border border-amber-300 rounded-md text-sm disabled:opacity-50 disabled:bg-amber-100"
                  data-testid="idv-expiration-date-input"
                />
                <label className="flex items-center gap-2 text-sm text-amber-900 cursor-pointer" data-testid="idv-no-expiration-row">
                  <input
                    type="checkbox"
                    checked={noExpiration}
                    onChange={(e) => { setNoExpiration(e.target.checked); if (e.target.checked) setIdExpiresAt(''); }}
                    className="rounded border-amber-400"
                    data-testid="idv-no-expiration-checkbox"
                  />
                  No expiration date
                </label>
              </div>
            </div>

            {/* Submit Button */}
            <Button
              className="w-full h-12 bg-cyan-500 hover:bg-cyan-600 text-white font-medium"
              onClick={handleSubmit}
              disabled={!documents.idFront || !documents.idBack || !documents.profilePhoto || uploading || (!noExpiration && !idExpiresAt)}
              data-testid="submit-idv-btn"
            >
              {uploading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Submit for Verification
                </>
              )}
            </Button>
            
            <p className="text-xs text-gray-500 text-center">
              Your documents will be reviewed by our team within 24-48 hours.
            </p>

            {/* Iter-220o — Always-on support fallback so users don't feel stuck. */}
            <div className="border-t border-gray-100 pt-4 text-center" data-testid="idv-support-fallback">
              <p className="text-xs text-gray-500 mb-1">Still having trouble?</p>
              <a
                href={`mailto:${SUPPORT_EMAIL}?subject=IDV%20Help%20Needed&body=I%20need%20help%20completing%20my%20ID%20verification.`}
                className="inline-flex items-center gap-1 text-sm font-medium text-cyan-700 hover:text-cyan-900"
                data-testid="idv-support-fallback-link"
              >
                <LifeBuoy className="w-4 h-4" /> Contact support
              </a>
            </div>
            </div>
          </CardContent>
        </Card>

        {/* Iter-212 — Background check status surface so talent can see
            Yardstik state without an admin telling them. */}
        <div className="mt-6">
          <TalentBackgroundCheckCard />
        </div>
      </div>
    </div>
  );
};

export default IDVerification;
